import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";

// -------------------------------
// SHARED TYPES AND UTILITIES
// -------------------------------
interface ExportOptions {
  fileName?: string;
  title?: string;
}

interface PDFExportOptions extends ExportOptions {
  showPageNumbers?: boolean;
  orientation?: "portrait" | "landscape";
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
}

interface PrintTableOptions extends ExportOptions {
  columns: any[];
}

/**
 * Extract text content from React nodes, elements, or any value
 */
function extractTextFromReactNode(node: any): string {
  if (node == null) return "";

  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (typeof node === "object" && node.props) {
    if (node.props.children) {
      return extractTextFromReactNode(node.props.children);
    }
    return "";
  }

  if (Array.isArray(node)) {
    return node.map((item) => extractTextFromReactNode(item)).join(" ");
  }

  return String(node);
}

/**
 * Escape HTML to prevent XSS and formatting issues
 */
function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Escape CSV fields to handle commas, quotes, and newlines
 */
function escapeCSVField(field: string): string {
  if (field === null || field === undefined) {
    return "";
  }

  const stringField = String(field);

  // If the field contains commas, quotes, or newlines, wrap it in quotes
  if (
    stringField.includes(",") ||
    stringField.includes('"') ||
    stringField.includes("\n") ||
    stringField.includes("\r")
  ) {
    // Escape existing quotes by doubling them
    const escapedField = stringField.replace(/"/g, '""');
    return `"${escapedField}"`;
  }

  return stringField;
}

/**
 * Prepare data for export (shared by all export functions)
 */
function prepareExportData(
  columns: any[],
  data: any[]
): {
  headers: string[];
  rows: string[][];
} {
  const headers = columns.map(
    (col) => col.title || String(col.dataIndex || col.key || "")
  );

  const rows = data.map((row) => {
    return columns.map((col) => {
      const dataKey = col.dataIndex || col.key;
      const rawValue = row[dataKey];

      // If column has a custom render function, use it
      if (col.render && typeof col.render === "function") {
        try {
          const renderedValue = col.render(rawValue, row);
          return extractTextFromReactNode(renderedValue);
        } catch (error) {
          console.warn(`Render failed for column ${col.title}:`, error);
          return String(rawValue ?? "");
        }
      }

      return String(rawValue ?? "");
    });
  });

  return { headers, rows };
}

/**
 * Download file helper (shared by all export functions)
 */
function downloadFile(content: BlobPart, fileName: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

// -------------------------------
// PRINT TABLE UTILITY
// -------------------------------
export function printTable(
  columns: any[],
  data: any[],
  options: PrintTableOptions = { columns: [] }
) {
  const { title = "Report", columns: columnDefs = columns } = options;
  const { headers, rows } = prepareExportData(columnDefs, data);

  const tableHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px;
            color: #333;
          }
          .print-title { 
            font-size: 24px; 
            font-weight: bold; 
            margin-bottom: 20px;
            text-align: center;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            font-size: 12px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 8px 12px; 
            text-align: left;
          }
          th { 
            background-color: #f8f9fa; 
            font-weight: bold;
            border-bottom: 2px solid #ddd;
          }
          tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        ${title ? `<div class="print-title">${title}</div>` : ""}
        
        <table>
          <thead>
            <tr>${headers
              .map((title) => `<th>${escapeHtml(title)}</th>`)
              .join("")}</tr>
          </thead>
          <tbody>
            ${rows
              .map(
                (row) => `
              <tr>
                ${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>

        <div class="no-print" style="margin-top: 20px; text-align: center;">
          <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Print Now
          </button>
          <button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">
            Close
          </button>
        </div>

        <script>
          window.onload = function() {
            setTimeout(() => window.print(), 500);
          };
        </script>
      </body>
    </html>
  `;

  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(tableHTML);
    printWindow.document.close();
    printWindow.focus();
  } else {
    alert("Please allow pop-ups for printing functionality.");
  }
}

// -------------------------------
// CSV EXPORT UTILITY
// -------------------------------
export function exportCSV(
  columns: any[],
  data: any[],
  fileName = "export.csv"
) {
  try {
    const { headers, rows } = prepareExportData(columns, data);

    const csvContent = [
      headers.map(escapeCSVField).join(","),
      ...rows.map((row) => row.map(escapeCSVField).join(",")),
    ].join("\n");

    downloadFile(csvContent, fileName, "text/csv;charset=utf-8;");
  } catch (error) {
    console.error("Error exporting to CSV:", error);
    exportCSVFallback(columns, data, fileName);
  }
}

function exportCSVFallback(
  columns: any[],
  data: any[],
  fileName = "export.csv"
) {
  const { headers, rows } = prepareExportData(columns, data);

  const csvContent = [
    headers.map(escapeCSVField).join(","),
    ...rows.map((row) => row.map(escapeCSVField).join(",")),
  ].join("\n");

  downloadFile(csvContent, fileName, "text/csv;charset=utf-8;");
}

// -------------------------------
// EXCEL EXPORT UTILITY
// -------------------------------
export function exportExcel(
  columns: any[],
  data: any[],
  fileName = "export.xlsx"
) {
  try {
    const { headers, rows } = prepareExportData(columns, data);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");

    // Add and style headers
    worksheet.addRow(headers);
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF2E86C1" },
    };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    // Add data rows
    rows.forEach((rowData) => worksheet.addRow(rowData));

    // Style data rows
    worksheet.columns = headers.map(() => ({ width: 15 }));
    for (let i = 2; i <= rows.length + 1; i++) {
      const row = worksheet.getRow(i);
      row.alignment = { vertical: "middle", horizontal: "left" };
      if (i % 2 === 0) {
        row.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF2F2F2" },
        };
      }
    }

    workbook.xlsx.writeBuffer().then((buffer) => {
      downloadFile(
        buffer,
        fileName,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
    });
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    exportExcelFallback(columns, data, fileName);
  }
}

function exportExcelFallback(
  columns: any[],
  data: any[],
  fileName = "export.xlsx"
) {
  const { headers, rows } = prepareExportData(columns, data);

  const tableHTML = `
    <table>
      <thead><tr>${headers
        .map((header) => `<th>${header}</th>`)
        .join("")}</tr></thead>
      <tbody>
        ${rows
          .map(
            (row) =>
              `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`
          )
          .join("")}
      </tbody>
    </table>
  `;

  downloadFile(tableHTML, fileName, "application/vnd.ms-excel");
}

// -------------------------------
// PDF EXPORT UTILITY
// -------------------------------
export function exportToPDF(
  columns: any[],
  data: any[],
  options: PDFExportOptions = {}
): void {
  const {
    fileName = `export_${Date.now()}.pdf`,
    title = "Report",
    showPageNumbers = true,
    orientation = "portrait",
    pagination,
  } = options;

  const { headers, rows } = prepareExportData(columns, data);
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });

  // Add title
  let startY = 15;
  if (title) {
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(title, 14, 12);
    startY = 22;
  }

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY,
    styles: { fontSize: 9, cellPadding: 3, textColor: [0, 0, 0] },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontStyle: "bold",
      fontSize: 10,
    },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { top: startY },
    didDrawPage: (data: any) => {
      // Pagination info (first page only)
      if (pagination && data.pageNumber === 1) {
        const { current, pageSize, total } = pagination;
        const startItem = (current - 1) * pageSize + 1;
        const endItem = Math.min(current * pageSize, total);
        const paginationText = `Showing ${startItem}-${endItem} of ${total} records`;

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100);

        const textWidth = doc.getTextWidth(paginationText);
        const xPosition = doc.internal.pageSize.width - textWidth - 14;
        doc.text(paginationText, xPosition, doc.internal.pageSize.height - 15);
      }

      // Page numbers
      if (showPageNumbers) {
        const pageCount = doc.getNumberOfPages();
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          data.settings.margin.left,
          doc.internal.pageSize.height - 10
        );
      }
    },
  });

  doc.save(fileName);
}
